import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";

import type { AdminEmailActivity } from "@recital-master/shared";

import { useAuthState } from "@/auth/AuthProvider";
import {
  buildAdminEmailActivity,
  type AdminAccountEmailRecord,
  type AdminEmailSourceRecord
} from "@/data/adminEmailActivity";
import { firestore } from "@/firebase/client";

export function useAdminEmailActivity({
  enabled,
  fallback
}: {
  enabled: boolean;
  fallback: AdminEmailActivity | null;
}): AdminEmailActivity | null {
  const { devMode, demoMode, isAdmin } = useAuthState();
  const preview = devMode || (__DEV__ && demoMode);
  const [messages, setMessages] = useState<AdminEmailSourceRecord[] | null>(null);
  const [accounts, setAccounts] = useState<AdminAccountEmailRecord[] | null>(null);

  useEffect(() => {
    if (!enabled || preview || !isAdmin || !firestore) {
      setMessages(null);
      setAccounts(null);
      return;
    }

    const unsubscribeMessages = onSnapshot(collection(firestore, "emailMessages"), (snapshot) => {
      setMessages(snapshot.docs.map((document) => ({
        id: document.id,
        data: document.data()
      })));
    });
    const unsubscribeAccounts = onSnapshot(collection(firestore, "familyAccounts"), (snapshot) => {
      setAccounts(snapshot.docs.map((document) => {
        const account = document.data();
        return {
          createdAt: account.createdAt,
          primaryEmail: account.primaryEmail
        };
      }));
    });
    return () => {
      unsubscribeMessages();
      unsubscribeAccounts();
    };
  }, [enabled, isAdmin, preview]);

  return useMemo(
    () => messages && accounts ? buildAdminEmailActivity(messages, accounts) : fallback,
    [accounts, fallback, messages]
  );
}
